import {
  Box,
  Button,
  Collapse,
  Dialog,
  Grid,
  IconButton,
  InputAdornment,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { CancelRounded } from '@material-ui/icons';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import { getUserPlayerList } from 'api/cms/player';
import { getProviderList } from 'api/cms/provider';
import Breadcrumbs from 'components/Breadcrumbs';
import ApplicationLayout from 'components/layouts/ApplicationLayout';
import useDebounce from 'hooks/useDebounce';
import queryString from 'query-string';
import { Fragment, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import useSWR from 'swr';
import TablePaginationActions from '../../../components/table/TablePaginationActions';
import { ProviderLinks } from '../../../utils/backoffice';
import { useDateRange } from '../../../utils/datetime';

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    width: '100%',
    overflowX: 'auto', // enable horizontal scroll if needed
  },
  table: {
    width: '100%',
    tableLayout: 'auto',
  },
  // Keep action buttons tidy; they won’t force the table wider
  actionCell: {
    whiteSpace: 'nowrap',
  },
  buttonGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap', // wrap on very small screens
  },
  actionBtn: {
    minWidth: 120, // compact buttons
  },
  // optional: tighten overall table density
  denseCell: {
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
  },
  inlineCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    maxWidth: 260, // adjust as needed
  },
  ellipsisText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1, // takes remaining space
  },
}));

const PlayerListPage = () => {
  const classes = useStyles();
  const [searchObj, setSearch] = useState({
    username: '',
    player_id: '',
  });
  const [gameProvider, setGameProvider] = useState('');
  const [expanded, setExpanded] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const searchDebounce = useDebounce(searchObj, 700);
  const [openDialog, setOpenDialog] = useState(false);
  const { search } = useLocation();
  const paramsString = queryString.parse(search);
  const [dateRange, setDateRange] = useDateRange(paramsString);
  const history = useHistory();

  const CopyIconButton = ({ value, title = 'Copy' }) => (
    <Tooltip title={title}>
      <IconButton size="small" onClick={() => copyToClipboard(value)} aria-label={title}>
        <FileCopyIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  const { data: players } = useSWR(
    [page, rowsPerPage, searchDebounce],
    (page, limit) =>
      getUserPlayerList({
        page,
        limit,
        username: searchDebounce?.username,
        player_id: searchDebounce?.player_id,
      }),
    {
      revalidateOnFocus: false,
      refreshInterval: 0,
      shouldRetryOnError: false,
    },
  );

  const handleClickOpen = () => {
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
    // setGameProvider(value);
  };

  const { data: providers } = useSWR('provider', () => getProviderList());

  function ProviderDialog(props) {
    const { onClose, open } = props;

    const handleClose = () => {
      onClose();
    };

    const handleListItemClick = () => {
      onClose();
    };

    return (
      <Dialog
        onClose={handleClose}
        aria-labelledby="provider-dialog-title"
        open={open}
        BackdropProps={{ invisible: true }}
        hideBackdrop={true}
        style={{ backgroundColor: 'rgba(0,0,0,0.3)' }}
        PaperProps={{
          elevation: 0,
        }}
      >
        {/*<DialogTitle id="provider-dialog-title">Set provider</DialogTitle>*/}
        <table>
          <thead>
            <tr>
              <th align={'left'}>Provider</th>
              <th>Prefix</th>
            </tr>
          </thead>
          <tbody>
            {providers?.map((row, index) => {
              return (
                <tr key={index}>
                  <td>
                    <a
                      href={`#${row.provider_code}`}
                      onClick={() => handleListItemClick(setGameProvider(row.provider_code))}
                    >
                      {row.provider_name}
                    </a>
                  </td>
                  <td>{row.provider_code}</td>
                  <td>
                    {ProviderLinks.filter((v) => v.provider_code === row.provider_code).map(
                      (p, i) =>
                        p.links.map((l, j) => {
                          return (
                            <a
                              key={`${i}${j}`}
                              href={l}
                              style={{ margin: '0 10px' }}
                              target="_blank"
                              rel="noreferrer"
                            >
                              Link
                            </a>
                          );
                        }),
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Dialog>
    );
  }

  useEffect(() => {
    if (players) {
      setExpanded([...Array(players.players.length)].map(() => false));
    }

    return () => {};
  }, [players]);

  const handleListTrxClick = (player_id) => {
    history.push(`/player_list/${player_id}?page=1&limit=25`);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    if (event.target.value === -1) {
      event.target.value = parseInt(0, 10);
      setRowsPerPage(0);
    } else {
      setRowsPerPage(parseInt(event.target.value, 10));
    }
    setPage(1);
  };

  const handleClick = (index) => {
    setExpanded(
      expanded.map((boolean_value, i) => {
        if (index === i) {
          return !boolean_value;
        } else {
          return false;
        }
      }),
    );
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then((r) => r);
    toast.info('Copied to clipboard');
  };

  const getGameUsername = (data, provider) => {
    const obj = ProviderLinks.find((v) => v.provider_code === provider);
    if (!obj) return '';
    if (obj.prefix !== undefined) {
      return obj.prefix === '' ? `${data.player_id}` : `${obj.prefix}${data.player_id}`;
    }
    return `${data.platform_code}${provider}${data.username}` || '';
  };

  // const searchParam = React.useMemo(() => {
  //   return genParamsQueryFromDateRange(dateRange);
  // }, [dateRange]);

  return (
    <ApplicationLayout>
      <Breadcrumbs />
      <Typography variant="h6" gutterBottom component="div">
        User Player List
      </Typography>
      <Box>
        <Paper style={{ padding: '15px', marginBottom: '15px' }}>
          <Grid item xs={12} justifyContent={'flex-start'} container>
            <Grid item xs={6}>
              <TextField
                size="small"
                variant="outlined"
                margin="normal"
                id="player_id"
                label="PlayerId"
                onChange={(e) => {
                  setSearch({
                    ...searchObj,
                    player_id: e.target.value,
                    username: undefined,
                  });
                }}
                InputProps={{
                  onClick: () => setSearch({ player_id: '' }),
                  endAdornment: (
                    <InputAdornment position="end">
                      <CancelRounded />
                    </InputAdornment>
                  ),
                }}
                value={searchObj?.player_id}
                defaultValue={searchObj?.player_id}
                style={{ marginRight: '15px' }}
              />
              <TextField
                size="small"
                variant="outlined"
                margin="normal"
                id="username"
                label="Username"
                onChange={(e) => {
                  setSearch({
                    ...searchObj,
                    username: e.target.value,
                    player_id: undefined,
                  });
                }}
                InputProps={{
                  onClick: () => setSearch({ username: '' }),
                  endAdornment: (
                    <InputAdornment position="end">
                      <CancelRounded />
                    </InputAdornment>
                  ),
                }}
                value={searchObj?.username}
                defaultValue={searchObj?.username}
              />
            </Grid>
          </Grid>
          <b>Example Username: 09xxxxxx (phonenumber)</b>
        </Paper>
        <Grid item xs={12}>
          <TableContainer className={classes.tableContainer}>
            <Table aria-label="table" className={classes.table} size="small">
              <TableHead>
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell>Player ID</TableCell>
                  <TableCell>Username</TableCell>
                  <TableCell>Platform</TableCell>
                  <TableCell>MerchantCode</TableCell>
                  <TableCell>Balance</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell>Online</TableCell>
                  <TableCell>GameUsername</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {players?.players.map((row, index) => (
                  <Fragment key={index}>
                    <TableRow key={`${index}_player`}>
                      <TableCell>
                        <IconButton
                          aria-label="expand row"
                          size="small"
                          onClick={() => handleClick(index)}
                        >
                          {expanded[index] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <div className={classes.inlineCell}>
                          <span className={classes.ellipsisText}>{row.player_id}</span>
                          <CopyIconButton value={row.player_id} title="Copy Player ID" />
                        </div>
                      </TableCell>
                      <TableCell>{row.username}</TableCell>
                      <TableCell>{row.platform_name}</TableCell>
                      <TableCell>{row.merchant_code}</TableCell>
                      <TableCell>{row.balance}</TableCell>
                      <TableCell>
                        {row.is_active ? (
                          <div style={{ color: 'green' }}>Yes </div>
                        ) : (
                          <div style={{ color: 'red' }}>'No' </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {row.is_online ? (
                          <div style={{ color: 'green' }}>Yes</div>
                        ) : (
                          <div style={{ color: 'red' }}>No</div>
                        )}
                      </TableCell>
                      {/* Game Username column */}
                      {(() => {
                        const gameUsername =
                          gameProvider !== '' ? getGameUsername(row, gameProvider) : '';
                        return (
                          <TableCell>
                            {gameUsername ? (
                              <div className={classes.inlineCell}>
                                <span className={classes.ellipsisText}>{gameUsername}</span>
                                <CopyIconButton value={gameUsername} title="Copy Game Username" />
                              </div>
                            ) : (
                              '' // nothing if undefined/empty
                            )}
                          </TableCell>
                        );
                      })()}
                      <TableCell>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            onClick={handleClickOpen}
                          >
                            Game Username
                          </Button>
                          <Button
                            onClick={() => handleListTrxClick(row.player_id)}
                            variant="contained"
                            color="primary"
                            size="small"
                          >
                            Transactions
                          </Button>
                        </div>
                        <ProviderDialog
                          selectedValue={gameProvider}
                          open={openDialog}
                          onClose={handleClose}
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0, border: 0 }} colSpan={9}>
                        <Collapse in={expanded[index]} timeout="auto" unmountOnExit={true}>
                          <Box sx={{ margin: 1 }}>
                            <Typography variant="h6" gutterBottom component="div">
                              User Information
                            </Typography>
                            <Table size="small" aria-label="purchases">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Game ID</TableCell>
                                  <TableCell>Game name</TableCell>
                                  <TableCell align="right">Category</TableCell>
                                  <TableCell align="right">Provider ID</TableCell>
                                  <TableCell align="right">Provider Code</TableCell>
                                  <TableCell align="right">Open date</TableCell>
                                  <TableCell align="right">Close date</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                <TableRow key={`${index}_information`}>
                                  <TableCell component="th" scope="row">
                                    {row.user_information.game_id}
                                  </TableCell>
                                  <TableCell>{row.user_information.game_name}</TableCell>
                                  <TableCell align="right">
                                    {row.user_information.category}
                                  </TableCell>

                                  <TableCell align="right">
                                    {row.user_information.provider_id}
                                  </TableCell>
                                  <TableCell align="right">
                                    {row.user_information.provider_code}
                                  </TableCell>
                                  <TableCell align="right">
                                    {row.user_information.open_game_date}
                                  </TableCell>
                                  <TableCell align="right">
                                    {row.user_information.close_game_date}
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[10, 50, 100]}
                    colSpan={8}
                    count={players?.total || 0}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    SelectProps={{
                      inputProps: { 'aria-label': 'rows per page' },
                      native: true,
                    }}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    ActionsComponent={TablePaginationActions}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </Grid>
      </Box>
    </ApplicationLayout>
  );
};

export default PlayerListPage;
